import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Flame, GoogleLogo, Envelope, Lock } from "@phosphor-icons/react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { STOIC_STATUE } from "@/lib/theme";

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("register"); // gate first -> encourage sign up
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") await register(email, password);
      else await login(email, password);
      toast.success(mode === "register" ? "Welcome to the Forge" : "Welcome back");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-stone-950 text-stone-50 flex">
      {/* Left visual */}
      <div className="hidden lg:block relative w-1/2 border-r border-stone-800">
        <img src={STOIC_STATUE} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25 grayscale" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/70 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-14">
          <Flame size={40} weight="fill" className="text-orange-700 mb-6" />
          <h2 className="font-serif-display text-4xl leading-tight text-stone-50">
            Five extracts.
            <br />
            Forged for the relentless.
          </h2>
          <p className="mt-4 text-stone-400 text-sm max-w-sm leading-relaxed">
            Daily wisdom from life-changing self-help books. Free, forever. Sign up to start
            forging your mind.
          </p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm"
        >
          <div className="flex items-center gap-2.5 lg:hidden mb-8">
            <Flame size={24} weight="fill" className="text-orange-700" />
            <span className="font-mono-ui text-sm tracking-[0.2em] uppercase">The Daily Forge</span>
          </div>

          <p className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-orange-600">
            {mode === "register" ? "Create your free account" : "Welcome back"}
          </p>
          <h1 className="mt-3 font-serif-display text-3xl sm:text-4xl text-stone-50">
            {mode === "register" ? "Start forging." : "Sign in."}
          </h1>
          <p className="mt-2 text-stone-500 text-sm">Completely free — no card, ever.</p>

          <button
            data-testid="google-login-button"
            onClick={handleGoogle}
            className="mt-8 w-full flex items-center justify-center gap-3 border border-stone-700 hover:border-stone-500 rounded-sm px-4 py-3.5 text-sm font-medium text-stone-100 transition-colors"
          >
            <GoogleLogo size={20} weight="bold" />
            Continue with Google
          </button>

          <div className="flex items-center gap-4 my-6">
            <span className="h-px flex-1 bg-stone-800" />
            <span className="font-mono-ui text-[10px] tracking-widest text-stone-600 uppercase">or</span>
            <span className="h-px flex-1 bg-stone-800" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="relative">
              <Envelope size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-500" />
              <input
                data-testid="email-input"
                type="email"
                required
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-stone-900 border border-stone-700 rounded-sm pl-11 pr-4 py-3.5 text-sm text-stone-50 placeholder-stone-600 focus:outline-none focus:border-orange-700"
              />
            </div>
            <div className="relative">
              <Lock size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-500" />
              <input
                data-testid="password-input"
                type="password"
                required
                minLength={6}
                placeholder="Password (min 6 chars)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-stone-900 border border-stone-700 rounded-sm pl-11 pr-4 py-3.5 text-sm text-stone-50 placeholder-stone-600 focus:outline-none focus:border-orange-700"
              />
            </div>
            <button
              data-testid="submit-auth-button"
              type="submit"
              disabled={busy}
              className="w-full bg-orange-800 hover:bg-orange-700 rounded-sm px-4 py-3.5 text-xs font-mono-ui uppercase tracking-[0.2em] text-stone-50 transition-colors disabled:opacity-50"
            >
              {busy ? "..." : mode === "register" ? "Create account" : "Sign in"}
            </button>
          </form>

          <p className="mt-6 text-sm text-stone-500 text-center">
            {mode === "register" ? "Already forging?" : "New here?"}{" "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => setMode(mode === "register" ? "login" : "register")}
              className="text-orange-500 hover:text-orange-400 font-medium"
            >
              {mode === "register" ? "Sign in" : "Create a free account"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
